'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUi } from '@weareconceptstudio/core';
import { closeBottomPopups } from '@/helpers/bottomPopups';

const OUTSIDE_CLICK_GRACE_MS = 400;
const BODY_CLASS = 'delivery-sticky-panel-open';

export const useStickyDeliveryPanel = () => {
	const [isOpen, setIsOpen] = useState(false);

	const rootRef = useRef(null);
	const openedAtRef = useRef(0);

	const { disableDomScroll, enableDomScroll } = useUi();

	const openPanel = useCallback(() => {
		openedAtRef.current = Date.now();
		setIsOpen(true);
		closeBottomPopups();
	}, []);

	const closePanel = useCallback(() => {
		setIsOpen(false);
	}, []);

	const togglePanel = useCallback((event) => {
		event?.stopPropagation?.();

		setIsOpen((prev) => {
			if (!prev) {
				openedAtRef.current = Date.now();
				closeBottomPopups();
			}

			return !prev;
		});
	}, []);

	const closePanelFromBackdrop = useCallback((event) => {
		if (event && !event.isTrusted) return;

		if (Date.now() - openedAtRef.current < OUTSIDE_CLICK_GRACE_MS) {
			return;
		}

		setIsOpen(false);
	}, []);

	// Lock body scroll
	useEffect(() => {
		if (isOpen) {
			disableDomScroll?.();
			document.body.classList.add(BODY_CLASS);
		} else {
			enableDomScroll?.();
			document.body.classList.remove(BODY_CLASS);
		}

		return () => {
			enableDomScroll?.();
			document.body.classList.remove(BODY_CLASS);
		};
	}, [isOpen, disableDomScroll, enableDomScroll]);

	// Outside click + Escape
	useEffect(() => {
		if (!isOpen) return;

		const isGracePeriod = () => Date.now() - openedAtRef.current < OUTSIDE_CLICK_GRACE_MS;

		const onPointerDown = (event) => {
			if (!event.isTrusted) return;
			if (isGracePeriod()) return;

			const target = event.target;

			if (!target) return;
			if (rootRef.current?.contains(target)) return;
			if (target.closest('.shop-delivery-panel-host')) return;
			if (target.closest('.sticky-delivery-panel')) return;
			if (target.closest('.sticky-delivery-backdrop')) return;
			// Global popups (e.g. "Add new address", opened from inside this panel via
			// useUi().openPopup()) render through <Popup /> mounted at the app root, well
			// outside this panel's DOM. Without this, clicking inside one of those popups
			// looks like an "outside click" and silently closes the panel underneath it —
			// dropping the scroll lock while the popup is still open on top.
			if (target.closest('.popup-content-container')) return;
			if (target.closest('.popup-background')) return;

			setIsOpen(false);
		};

		const onKeyDown = (event) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
			}
		};

		const timer = setTimeout(() => {
			document.addEventListener('pointerdown', onPointerDown, true);
			document.addEventListener('keydown', onKeyDown);
		}, 0);

		return () => {
			clearTimeout(timer);
			document.removeEventListener('pointerdown', onPointerDown, true);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen]);

	return {
		isOpen,
		rootRef,

		openPanel,
		closePanel,
		togglePanel,
		closePanelFromBackdrop,
	};
};
